import { getI18nRoute } from "src/utils/i18nRoutes";
import {
  isGender,
  localeFix,
  ShopType,
} from "src/utils/genderLocale/genderLocale";
import { ROOT_PARENT_ID } from "src/utils/category-tree-helpers";
import type { CategoryNodeType } from "src/molecules/CategoryTree/types";
import type { TreeType } from "src/types/Tree";
import type { Locale } from "src/lib/Intl";

export type CategoryLevelItem = {
  itemId: string;
  label: string;
  slug: string;
  href: string;
  isActive: boolean;
};

export type CategoryLevelView = {
  items: CategoryLevelItem[];
  back?: { label: string; href: string };
  /** the focused category (the PLP we're on), absent on brand/search/landing */
  current?: { label: string; href: string };
  /** true when `items` are the focus's siblings (focus has no children) */
  isLeaf: boolean;
};

type GetCategoryLevelViewArgs = {
  tree: TreeType<CategoryNodeType>;
  gender: ShopType;
  locale: Locale;
  categorySlug?: string;
  buildHref?: (slug: string) => string;
  rootBack?: { label: string; href: string };
  treatAsLeaf?: boolean;
};

const GENDER_ORDER = new Map<string, number>([
  [ShopType.Women, 0],
  [ShopType.Men, 1],
  [ShopType.Kids, 2],
]);

/**
 * Department roots (Women -> Men -> Kids) come first in that fixed order;
 * regular categories follow alphabetically in the storefront's locale.
 */
export function sortCategoryNodes(
  nodes: CategoryNodeType[],
  locale: Locale
): CategoryNodeType[] {
  const rank = (node: CategoryNodeType): number =>
    isGender(node.slug)
      ? GENDER_ORDER.get(localeFix({ gender: node.slug })) ??
        Number.MAX_SAFE_INTEGER
      : Number.MAX_SAFE_INTEGER;

  return [...nodes].sort((a, b) => {
    const rankDiff = rank(a) - rank(b);
    return rankDiff !== 0
      ? rankDiff
      : a.label.localeCompare(b.label, locale, { sensitivity: "base" });
  });
}

export function categoryHref(
  slug: string,
  gender: ShopType,
  locale: Locale
): string {
  return gender && gender !== ShopType.All
    ? `${getI18nRoute({ route: `/${gender}`, locale })}/${slug}`
    : `/products/${slug}`;
}

/**
 * Derives the single drill-down level to render from the flat category tree.
 * - focus = node matching `categorySlug` (the category PLP we're on)
 * - focus has children -> list its children (drill deeper), nothing active
 * - focus is a leaf -> list its siblings, focus is active (underlined)
 * - back row points at focus's parent whenever one exists, so every drill is
 *   reversible; only the top of the tree (a root, or no focus) has no back row
 * - no focus: gendered -> the department's top-level categories; genderless/brand
 *   -> the department roots themselves (Men/Women/Kids)
 * - items are ordered Women -> Men -> Kids for department roots, alphabetically
 *   otherwise (see sortCategoryNodes)
 */
export function getCategoryLevelView({
  tree,
  gender,
  locale,
  categorySlug,
  buildHref,
  rootBack,
  treatAsLeaf = false,
}: GetCategoryLevelViewArgs): CategoryLevelView {
  if (!tree.length) {
    return { items: [], isLeaf: false };
  }

  const byId = new Map(tree.map((node) => [node.itemId, node]));
  const childrenOf = (parentId: string): CategoryNodeType[] =>
    tree.filter((node) => node.parentId === parentId);
  const isDepartmentRoot = (node?: CategoryNodeType): boolean =>
    !node || node.parentId === ROOT_PARENT_ID;

  const toHref = (slug: string): string =>
    buildHref ? buildHref(slug) : categoryHref(slug, gender, locale);

  const toItem = (
    node: CategoryNodeType,
    activeSlug?: string
  ): CategoryLevelItem => ({
    itemId: node.itemId,
    label: node.label,
    slug: node.slug,
    href: toHref(node.slug),
    isActive: node.slug === activeSlug,
  });

  // Genderless / brand listings keep all department roots (Men/Women/Kids) in
  // the tree, so the drill-down starts at those roots. A gendered listing is
  // already scoped to one department, which the header reflects, so it starts
  // one level down at that department's categories.
  const genderless = gender === ShopType.All;

  const focus = categorySlug
    ? tree.find((node) => node.slug === categorySlug)
    : undefined;

  if (!focus) {
    const topLevel = tree.filter((node) =>
      genderless
        ? isDepartmentRoot(node)
        : isDepartmentRoot(byId.get(node.parentId)) && !isDepartmentRoot(node)
    );
    return {
      items: sortCategoryNodes(topLevel, locale).map((node) => toItem(node)),
      isLeaf: false,
    };
  }

  const children = childrenOf(focus.itemId);
  const isLeaf = treatAsLeaf || children.length === 0;
  const items = sortCategoryNodes(
    isLeaf ? childrenOf(focus.parentId) : children,
    locale
  ).map((node) => toItem(node, isLeaf ? focus.slug : undefined));

  // Always offer a way back up while there is a parent in the tree, so every
  // drill is reversible. A focus with no parent falls back to `rootBack` when
  // provided (search: clear the filter); otherwise it's the top of the tree.
  const parent = byId.get(focus.parentId);
  const back = parent
    ? {
        label: parent.label,
        href: toHref(parent.slug),
      }
    : rootBack;

  const current = {
    label: focus.label,
    href: toHref(focus.slug),
  };

  return { items, back, current, isLeaf };
}
